import { createContext, useContext, useEffect, useState, type ReactNode } from 'react'

export type AccentKey = 'indigo' | 'teal' | 'mono'

interface AccentTheme {
  key: AccentKey
  hex: string
  tint: string // hex @ ~9% alpha
}

const THEMES: Record<AccentKey, AccentTheme> = {
  indigo: { key: 'indigo', hex: '#4F46E5', tint: 'rgba(79,70,229,0.09)' },
  teal:   { key: 'teal',   hex: '#0E7C66', tint: 'rgba(14,124,102,0.09)' },
  mono:   { key: 'mono',   hex: '#101012', tint: 'rgba(16,16,18,0.09)' },
}

// Weighted selection: indigo 45%, teal 45%, mono 10%
function randomTheme(): AccentTheme {
  const r = Math.random()
  if (r < 0.45) return THEMES.indigo
  if (r < 0.90) return THEMES.teal
  return THEMES.mono
}

function applyTheme(theme: AccentTheme) {
  const root = document.documentElement
  root.style.setProperty('--accent', theme.hex)
  root.style.setProperty('--accent-tint', theme.tint)
}

// ─── Context ────────────────────────────────────────────────────────────────

const AccentThemeCtx = createContext<AccentTheme>(THEMES.mono)

export function useAccentTheme() {
  return useContext(AccentThemeCtx)
}

// ─── Provider ────────────────────────────────────────────────────────────────

export function AccentThemeProvider({ children }: { children: ReactNode }) {
  // Theme is picked once at mount — no setter needed, so we use a ref-style init.
  const [theme] = useState<AccentTheme>(() => {
    const picked = randomTheme()
    applyTheme(picked)
    return picked
  })

  // Re-apply on every mount (handles HMR in dev too)
  useEffect(() => {
    applyTheme(theme)
  }, [theme])

  return (
    <AccentThemeCtx.Provider value={theme}>
      {children}
    </AccentThemeCtx.Provider>
  )
}
